import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'

function describedBy(
  id: string,
  error: string | undefined,
  hint: string | undefined,
): string | undefined {
  return [error ? `${id}-error` : null, hint ? `${id}-hint` : null]
    .filter(Boolean)
    .join(' ') || undefined
}

interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  error?: string
  hint?: string
  trailing?: ReactNode
}

export function TextField({
  label,
  error,
  hint,
  trailing,
  className,
  ...inputProps
}: TextFieldProps) {
  const id = useId()
  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <div className={trailing ? 'relative' : undefined}>
        <input
          {...inputProps}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, hint)}
          className={[
            'field-input',
            error ? 'border-danger' : '',
            trailing ? 'pr-12' : '',
            className ?? '',
          ]
            .join(' ')
            .trim()}
        />
        {trailing ? (
          <span className="absolute inset-y-0 right-1 flex items-center">
            {trailing}
          </span>
        ) : null}
      </div>
      {hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-xs leading-snug text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-sm font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}

interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string
  error?: string
  hint?: string
}

export function TextAreaField({
  label,
  error,
  hint,
  className,
  ...textareaProps
}: TextAreaFieldProps) {
  const id = useId()
  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <textarea
        {...textareaProps}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={[
          'field-input resize-y',
          error ? 'border-danger' : '',
          className ?? '',
        ]
          .join(' ')
          .trim()}
      />
      {hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-xs leading-snug text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-sm font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}

interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  error?: string
  hint?: string
  children: ReactNode
}

export function SelectField({
  label,
  error,
  hint,
  children,
  className,
  ...selectProps
}: SelectFieldProps) {
  const id = useId()
  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select
        {...selectProps}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={[
          'field-input',
          error ? 'border-danger' : '',
          className ?? '',
        ]
          .join(' ')
          .trim()}
      >
        {children}
      </select>
      {hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-xs leading-snug text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 text-sm font-semibold text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
